import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "../../lib/api";
import { CONTRACT_STATUS, fmtDate, RISK } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, EmptyState, PageHeader, ToneChip } from "../../components/common";

export default function NdaLoi() {
  const navigate = useNavigate();
  const { map } = useUsers();
  const { data: ndas, isLoading: l1 } = useQuery({
    queryKey: ["ndas"],
    queryFn: async () => (await api.get("/legal/contracts", { params: { type: "Confidentiality Agreement" } })).data,
  });
  const { data: lois, isLoading: l2 } = useQuery({
    queryKey: ["lois"],
    queryFn: async () => (await api.get("/legal/contracts", { params: { type: "Letter of Intent" } })).data,
  });

  const Column = ({ title, items, kind }) => (
    <section data-testid={`${kind}-section`}>
      <h2 className="ts-section-title mb-3">{title} <span className="text-sm font-normal text-slate-400">({items?.length || 0})</span></h2>
      <div className="space-y-2">
        {(items || []).map((c) => (
          <button key={c.id} onClick={() => navigate(`/legal/contratos/${c.id}`)} className="ts-card ts-card-hover block w-full p-4 text-left" data-testid={`${kind}-card-${c.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <p className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900">{c.title}</p>
              <Chip map={CONTRACT_STATUS} value={c.status} />
            </div>
            <p className="mt-1 font-mono text-xs text-slate-400">{c.contract_number}</p>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
              <span>Owner: {userName(map, c.contract_owner)}</span>
              {kind === "nda" && c.nda && (
                <>
                  <span>{c.nda.mutual ? "Mutuo" : "Unilateral"}</span>
                  <span>Fin de confidencialidad: {fmtDate(c.nda.confidentiality_end)}</span>
                  <span>Data room: <strong className={c.nda.data_room_access ? "text-emerald-600" : ""}>{c.nda.data_room_access ? "activo" : "inactivo"}</strong></span>
                </>
              )}
              {kind === "loi" && c.loi && (
                <>
                  <span>Precio indicativo: <strong>{(c.loi.indicative_price / 1000000).toFixed(1)} M€</strong></span>
                  <span>Exclusividad hasta: <strong className="text-amber-700">{fmtDate(c.loi.exclusivity_end)}</strong></span>
                  <span>Gate: {c.loi.next_gate}</span>
                </>
              )}
              <span>Vence: {fmtDate(c.current_expiration_date)}</span>
            </div>
          </button>
        ))}
        {!items?.length && <p className="text-sm text-slate-400">Sin elementos.</p>}
      </div>
    </section>
  );

  return (
    <div data-testid="nda-loi-page">
      <PageHeader title="NDA y LOI" subtitle="Especializaciones contractuales con flujos propios: NDA → acceso documental; LOI → expediente dependiente de Due Diligence." />
      {l1 || l2 ? (
        <p className="py-10 text-center text-sm text-slate-500">Cargando…</p>
      ) : (
        <div className="grid gap-6 xl:grid-cols-2">
          <Column title="Acuerdos de confidencialidad (NDA)" items={ndas} kind="nda" />
          <Column title="Cartas de intención (LOI)" items={lois} kind="loi" />
        </div>
      )}
    </div>
  );
}
